import { FC, useCallback, useState } from 'react';
import { Button, Form, Input, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuthDispatchContext } from 'providers/Auth';
import { Link, useNavigate } from 'react-router-dom';

import { LoginContent, LinkWrapper } from './styled';
import { refreshTokenStorage, tokenStorage } from 'utils/storage';
import { showRestError } from 'utils/showErrorMessage';

const { Title } = Typography;

const Login: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { signIn, getUserData } = useAuthDispatchContext();
  const [isLoading, setLoading] = useState(false);

  const onFinish = useCallback(
    values => {
      setLoading(true);
      signIn(values)
        .then(data => {
          if (data.auth2fa) {
            return navigate('/auth2fa');
          }

          if (data.accessToken) {
            tokenStorage.set(data.accessToken);
          }

          if (data.refreshToken) {
            refreshTokenStorage.set(data.refreshToken);
          }

          getUserData();
        })
        .catch(e => {
          setLoading(false);

          // 422 → field errors on the form; anything else (400 wrong
          // credentials, etc.) the interceptor rejects as a message string —
          // surface it instead of failing silently.
          if (e?.errors) {
            form.setFields(e?.errors);
          } else {
            showRestError(e);
          }
        });
    },
    [signIn, getUserData, navigate, form]
  );

  return (
    <LoginContent>
      <Title level={2} style={{ marginBottom: 24 }}>
        {t('auth.welcome')}
      </Title>
      <Form form={form} layout="vertical" size="large" onFinish={onFinish}>
        <Form.Item
          name="email"
          rules={[{ required: true }, { type: 'email' }]}
          style={{ marginBottom: 16 }}
          messageVariables={{ label: t('auth.email') }}
        >
          <Input placeholder={t('auth.email')} />
        </Form.Item>
        <Form.Item
          name="password"
          rules={[{ required: true }]}
          style={{ marginBottom: 8 }}
          messageVariables={{ label: t('auth.password') }}
        >
          <Input.Password placeholder={t('auth.password')} />
        </Form.Item>
        <LinkWrapper>
          <Link to="/forget-password">{t('auth.forgot_password')}</Link>
        </LinkWrapper>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            shape="round"
            loading={isLoading}
            block
          >
            {t('auth.login')}
          </Button>
        </Form.Item>
      </Form>
    </LoginContent>
  );
};

export default Login;
